/**
 * permissoes.js — dica de permissão para a UI (habilitar/desabilitar botões).
 *
 * A API é quem decide (403); aqui só se evita oferecer o que vai falhar. Não há endpoint
 * de "minhas permissões": a dica vem do JWT.
 *   1. claim `permissions` (lista "recurso:acao"), se o token trouxer;
 *   2. senão, o claim `matrix_id` (o SSO grava o SLUG da matriz — services/sso.py) contra
 *      a matriz-semente da migration 0002 (identity_rbac).
 * Matriz customizada (UUID ou slug desconhecido) → sem dica → botão desabilitado; a API
 * continua sendo a autoridade (EC-7).
 */
import { useAuth } from '@/auth/AuthContext';

/** `create_edit` por matriz-semente (0002): admin tudo; manager e analyze incluem o catálogo. */
const CREATE_EDIT_POR_MATRIZ = {
  'm-admin': null,                     // null = todos os recursos
  'm-manager': ['agent_catalog', 'dashboard_projects', 'squads_designer', 'quality_gates',
    'live_board', 'data_sources', 'budget_costs'],
  'm-analyze': ['dashboard_projects', 'squads_designer', 'agent_catalog', 'live_board',
    'data_sources'],
  'm-estimative': [],
  'm-viewer': [],
};

/** Pura: o usuário (claims do JWT) pode `acao` em `recurso`? */
export function podeFazer(user, recurso, acao) {
  if (!user) return false;
  const perms = user.permissions;
  if (Array.isArray(perms)) return perms.includes(`${recurso}:${acao}`);
  if (acao !== 'create_edit') return false;
  const slug = String(user.matrix_id || '');
  if (!(slug in CREATE_EDIT_POR_MATRIZ)) return false;
  const lista = CREATE_EDIT_POR_MATRIZ[slug];
  return lista === null || lista.includes(recurso);
}

/** Usuário da sessão, ou null fora do AuthProvider (testes de tela isolada). */
function useUsuarioOpcional() {
  try {
    return useAuth()?.user ?? null;
  } catch {
    return null;
  }
}

/** Pode editar o catálogo (`agent_catalog:create_edit`)? Habilita "promover ao tenant". */
export function usePodeEditarCatalogo() {
  return podeFazer(useUsuarioOpcional(), 'agent_catalog', 'create_edit');
}
